import { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabase';
import { VENDORS, vendorOf, generateId } from './constants';
import Clients from './Clients';

// ─── Theme helpers ───────────────────────────────────────────────────────────

function getT(theme) {
  const dark = theme === 'dark';
  return {
    bgPrimary:   dark ? '#0e1015'  : '#ffffff',
    bgSecondary: dark ? '#0b0e14'  : '#f0f0f0',
    bgCard:      dark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
    bgInput:     dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
    bgInputDark: dark ? '#2a2d36'  : '#e8e8e8',
    bgModal:     dark ? '#181b22'  : '#ffffff',
    bgButton:    dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)',
    border:      dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.1)',
    borderMid:   dark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.15)',
    text:        dark ? '#ffffff'  : '#0e1015',
    textSec:     dark ? 'rgba(255,255,255,0.5)'  : 'rgba(0,0,0,0.5)',
    textMuted:   dark ? 'rgba(255,255,255,0.3)'  : 'rgba(0,0,0,0.3)',
    textFaint:   dark ? 'rgba(255,255,255,0.2)'  : 'rgba(0,0,0,0.2)',
    textLabel:   dark ? 'rgba(255,255,255,0.4)'  : 'rgba(0,0,0,0.4)',
    divider:     dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.07)',
    actionBg:    dark ? '#ffffff'  : '#0e1015',
    actionText:  dark ? '#0e1015' : '#ffffff',
    sidebarBorder: dark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.1)',
  };
}

// Current theme state - updated by Admin component
let currentTheme = localStorage.getItem('gearlist_theme') || 'dark';
const getTheme = () => getT(currentTheme);

// ─── Small helpers ────────────────────────────────────────────────────────────

function VendorPicker({ value, onChange }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
      {VENDORS.map(v => (
        <button key={v.id} onClick={() => onChange(v.id)} style={{
          padding: '8px 4px', borderRadius: 10, fontSize: 12, fontWeight: 600,
          cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
          background: value === v.id ? v.bg : getTheme().bgButton,
          color: value === v.id ? v.color : getTheme().textSec,
          border: `1px solid ${value === v.id ? v.border : getTheme().borderMid}`,
        }}>{v.name}</button>
      ))}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label style={{ fontSize: 11, color: getTheme().textLabel, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

const inputStyle = (extra = {}) => ({
  width: '100%', background: getTheme().bgInput,
  border: `1px solid ${getTheme().borderMid}`, borderRadius: 12,
  padding: '11px 14px', fontSize: 14, color: getTheme().text,
  outline: 'none', fontFamily: 'inherit', ...extra
});

// ─── Add / Edit Modal ─────────────────────────────────────────────────────────

function ItemModal({ item, onSave, onClose }) {
  const isEdit = !!(item && item.name);
  const [form, setForm] = useState(item || {
    name: '', sku: '', vendor: 'ubiquiti', url: '', qty: 1, unitPrice: '', notes: ''
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState('');

  const guessVendor = (url) => {
    const u = url.toLowerCase();
    if (u.includes('ui.com') || u.includes('ubiquiti')) return 'ubiquiti';
    if (u.includes('amazon.')) return 'amazon';
    if (u.includes('homedepot.')) return 'homedepot';
    if (u.includes('bestbuy.')) return 'bestbuy';
    if (u.includes('newegg.')) return 'newegg';
    return null;
  };

  const fetchFromUrl = async () => {
    if (!form.url.trim()) return;
    setFetching(true);
    setFetchError('');
    try {
      const res = await fetch(`/api/fetch-product?url=${encodeURIComponent(form.url.trim())}`);
      const data = await res.json();
      if (data.error || (!data.name && !data.price && !data.sku)) {
        setFetchError('Could not fetch — fill in manually.');
        setFetching(false);
        return;
      }
      if (data.name) set('name', data.name.substring(0, 80));
      if (data.sku) set('sku', data.sku.substring(0, 40));
      if (data.price) set('unitPrice', String(parseFloat(data.price) || ''));
      if (data.description) set('notes', data.description.substring(0, 200));
      const vendor = guessVendor(form.url);
      if (vendor) set('vendor', vendor);
    } catch (e) {
      setFetchError('Could not fetch — fill in manually.');
    }
    setFetching(false);
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20
    }}>
      <div onClick={e => e.stopPropagation()} className="fade-in" style={{
        background: getTheme().bgModal, width: '100%', maxWidth: 480,
        borderRadius: 20, padding: 24, maxHeight: '90vh', overflowY: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ fontSize: 17, fontWeight: 700, color: getTheme().text }}>{isEdit ? 'Edit Item' : 'Add Item'}</h2>
          <button onClick={onClose} style={{
            width: 30, height: 30, borderRadius: '50%', background: getTheme().bgButton,
            border: 'none', color: getTheme().textSec, cursor: 'pointer', fontSize: 16
          }}>×</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field label="Vendor"><VendorPicker value={form.vendor} onChange={v => set('vendor', v)} /></Field>

          <Field label="Item Name *">
            <input autoFocus style={inputStyle()} placeholder="e.g. UniFi G6 Turret"
              value={form.name} onChange={e => set('name', e.target.value)} />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="SKU / Model">
              <input style={inputStyle()} placeholder="UVC-G6-Turret"
                value={form.sku} onChange={e => set('sku', e.target.value)} />
            </Field>
            <Field label="Unit Price">
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: getTheme().textMuted, fontSize: 14 }}>$</span>
                <input type="number" min="0" step="0.01" style={inputStyle({ paddingLeft: 28 })}
                  placeholder="0.00" value={form.unitPrice} onChange={e => set('unitPrice', e.target.value)} />
              </div>
            </Field>
          </div>

          <Field label="Quantity">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button onClick={() => set('qty', Math.max(1, (parseInt(form.qty)||1) - 1))} style={{
                width: 40, height: 40, borderRadius: 10, background: getTheme().bgButton,
                border: 'none', color: getTheme().text, fontSize: 20, cursor: 'pointer', fontFamily: 'inherit'
              }}>−</button>
              <span style={{ fontSize: 20, fontWeight: 700, width: 32, textAlign: 'center' }}>{form.qty}</span>
              <button onClick={() => set('qty', (parseInt(form.qty)||1) + 1)} style={{
                width: 40, height: 40, borderRadius: 10, background: getTheme().bgButton,
                border: 'none', color: getTheme().text, fontSize: 20, cursor: 'pointer', fontFamily: 'inherit'
              }}>+</button>
            </div>
          </Field>

          <Field label="Product URL">
            <div style={{ display: 'flex', gap: 8 }}>
              <input style={inputStyle({ flex: 1 })} placeholder="https://store.ui.com/..."
                value={form.url} onChange={e => set('url', e.target.value)} />
              <button onClick={fetchFromUrl} disabled={!form.url.trim() || fetching} style={{
                padding: '0 14px', borderRadius: 12, border: 'none', flexShrink: 0,
                background: form.url.trim() ? getTheme().actionBg : getTheme().bgButton,
                color: form.url.trim() ? getTheme().actionText : getTheme().textMuted,
                fontSize: 12, fontWeight: 700, cursor: form.url.trim() ? 'pointer' : 'not-allowed',
                fontFamily: 'inherit', whiteSpace: 'nowrap',
              }}>{fetching ? '...' : '✨ Auto-fill'}</button>
            </div>
            {fetchError && <p style={{ fontSize: 12, color: '#f87171', marginTop: 6 }}>{fetchError}</p>}
          </Field>

          <Field label="Notes">
            <input style={inputStyle()} placeholder="Color, size, special instructions..."
              value={form.notes} onChange={e => set('notes', e.target.value)} />
          </Field>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 20 }}>
          <button onClick={onClose} style={{
            padding: 13, borderRadius: 12, border: `1px solid ${getTheme().borderMid}`,
            background: 'transparent', color: getTheme().textSec, fontSize: 14,
            fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit'
          }}>Cancel</button>
          <button onClick={() => { if (!form.name.trim()) return; onSave(form); onClose(); }}
            disabled={!form.name.trim()} style={{
              padding: 13, borderRadius: 12, border: 'none',
              background: form.name.trim() ? getTheme().actionBg : getTheme().bgButton,
              color: form.name.trim() ? getTheme().actionText : getTheme().textMuted,
              fontSize: 14, fontWeight: 700, cursor: form.name.trim() ? 'pointer' : 'not-allowed',
              fontFamily: 'inherit', transition: 'all 0.15s'
            }}>{isEdit ? 'Save Changes' : 'Add Item'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Share Modal ──────────────────────────────────────────────────────────────

function ShareModal({ shareUrl, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 100, padding: 24
    }}>
      <div onClick={e => e.stopPropagation()} className="fade-in" style={{
        background: getTheme().bgModal, width: '100%', maxWidth: 400,
        borderRadius: 20, padding: 28
      }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12, background: 'rgba(34,197,94,0.15)',
            border: '1px solid rgba(34,197,94,0.3)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px'
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2">
              <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
            </svg>
          </div>
          <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>Share this list</h2>
          <p style={{ fontSize: 13, color: getTheme().textLabel, lineHeight: 1.5 }}>
            Anyone with this link can view the list and open product links — but can't edit anything.
          </p>
        </div>

        <div style={{
          background: getTheme().bgInput, border: `1px solid ${getTheme().borderMid}`,
          borderRadius: 12, padding: '12px 14px', marginBottom: 12,
          fontSize: 12, color: getTheme().textSec,
          wordBreak: 'break-all', lineHeight: 1.6
        }}>
          {shareUrl}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <button onClick={onClose} style={{
            padding: 12, borderRadius: 12, border: `1px solid ${getTheme().borderMid}`,
            background: 'transparent', color: getTheme().textSec,
            fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit'
          }}>Close</button>
          <button onClick={copy} style={{
            padding: 12, borderRadius: 12, border: 'none',
            background: copied ? 'rgba(34,197,94,0.2)' : 'white',
            color: copied ? '#22c55e' : '#0e1015',
            fontSize: 14, fontWeight: 700, cursor: 'pointer',
            fontFamily: 'inherit', transition: 'all 0.2s',
            border: copied ? '1px solid rgba(34,197,94,0.4)' : 'none'
          }}>{copied ? '✓ Copied!' : 'Copy Link'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Vendor Group ─────────────────────────────────────────────────────────────

function VendorGroup({ vendor, items, onEdit, onRemove, onToggle }) {
  const v = vendorOf(vendor);
  const subtotal = items.reduce((s, i) => s + (parseFloat(i.unitPrice)||0) * (parseInt(i.qty)||1), 0);
  const withLinks = items.filter(i => i.url);
  const checkedCount = items.filter(i => i.checked).length;

  const openAll = () => withLinks.forEach((item, idx) =>
    setTimeout(() => window.open(item.url, '_blank'), idx * 350)
  );

  return (
    <div style={{ borderRadius: 18, overflow: 'hidden', border: `1px solid ${v.border}` }}>
      {/* Header */}
      <div style={{ background: v.bg, padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8, background: v.color,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 700, color: getTheme().text
          }}>{v.icon}</div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: v.color }}>{v.name}</div>
            <div style={{ fontSize: 11, color: getTheme().textMuted }}>
              {items.length} item{items.length !== 1 ? 's' : ''}
              {checkedCount > 0 && ` · ${checkedCount} ordered`}
              {subtotal > 0 && ` · $${subtotal.toFixed(2)}`}
            </div>
          </div>
        </div>
        {withLinks.length > 0 && (
          <button onClick={openAll} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '7px 14px', borderRadius: 10, border: 'none',
            background: v.color, color: getTheme().text, fontSize: 12,
            fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit'
          }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
              <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
            </svg>
            Open all {withLinks.length > 1 ? `(${withLinks.length})` : ''}
          </button>
        )}
      </div>

      {/* Items */}
      <div style={{ background: getTheme().bgSecondary }}>
        {items.map((item, idx) => {
          const lineTotal = item.unitPrice
            ? (parseFloat(item.unitPrice) * (parseInt(item.qty)||1)).toFixed(2)
            : null;

          return (
            <div key={item.id} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '13px 16px',
              borderTop: idx > 0 ? '1px solid rgba(255,255,255,0.05)' : 'none',
              opacity: item.checked ? 0.4 : 1, transition: 'opacity 0.2s'
            }}>
              {/* Checkbox */}
              <button onClick={() => onToggle(item.id)} style={{
                width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                border: `2px solid ${item.checked ? '#22c55e' : getTheme().borderMid}`,
                background: item.checked ? '#22c55e' : 'transparent',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s'
              }}>
                {item.checked && (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                )}
              </button>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{
                    fontSize: 14, fontWeight: 600, color: getTheme().text,
                    textDecoration: item.checked ? 'line-through' : 'none'
                  }}>{item.name}</span>
                  {item.sku && <span style={{ fontSize: 11, fontFamily: 'monospace', color: getTheme().textFaint }}>{item.sku}</span>}
                </div>
                {item.notes && <p style={{ fontSize: 12, color: getTheme().textMuted, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.notes}</p>}
                {item.url && (
                  <a href={item.url} target="_blank" rel="noopener noreferrer" style={{
                    fontSize: 11, color: v.color, opacity: 0.5, display: 'block',
                    marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    textDecoration: 'none'
                  }}
                  onMouseOver={e => e.target.style.opacity = 1}
                  onMouseOut={e => e.target.style.opacity = 0.5}
                  >{item.url.replace(/^https?:\/\//, '').slice(0, 44)}{item.url.length > 49 ? '…' : ''}</a>
                )}
              </div>

              <div style={{ textAlign: 'center', flexShrink: 0 }}>
                <div style={{ fontSize: 11, color: getTheme().textMuted }}>×{item.qty}</div>
              </div>

              {lineTotal && (
                <div style={{ textAlign: 'right', flexShrink: 0, minWidth: 52 }}>
                  <span style={{ fontSize: 14, fontWeight: 700 }}>${lineTotal}</span>
                </div>
              )}

              {/* Actions */}
              <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                <button onClick={() => onEdit(item)} style={{
                  width: 30, height: 30, borderRadius: 8, border: 'none',
                  background: getTheme().bgInput, color: getTheme().textLabel,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                </button>
                <button onClick={() => onRemove(item.id)} style={{
                  width: 30, height: 30, borderRadius: 8, border: 'none',
                  background: getTheme().bgInput, color: getTheme().textLabel,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── New List Modal ──────────────────────────────────────────────────────────

function NewListModal({ clients, onCreate, onClose }) {
  const [name, setName] = useState('');
  const [clientId, setClientId] = useState('');

  const handleCreate = () => {
    onCreate(name.trim() || null, clientId || null);
    onClose();
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: getTheme().bgModal, width: '100%', maxWidth: 480,
        borderRadius: 20, padding: 28
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ fontSize: 17, fontWeight: 700, color: getTheme().text }}>New List</h2>
          <button onClick={onClose} style={{
            width: 30, height: 30, borderRadius: '50%', background: getTheme().bgButton,
            border: 'none', color: getTheme().textSec, cursor: 'pointer', fontSize: 18
          }}>×</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ fontSize: 11, color: getTheme().textLabel, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>List Name</label>
            <input
              autoFocus
              style={{
                width: '100%', background: getTheme().bgInput,
                border: `1px solid ${getTheme().borderMid}`, borderRadius: 12,
                padding: '11px 14px', fontSize: 14, color: getTheme().text,
                outline: 'none', fontFamily: 'inherit'
              }}
              placeholder="e.g. Harbor View Hotel — AP Install"
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCreate()}
            />
          </div>

          {clients.length > 0 && (
            <div>
              <label style={{ fontSize: 11, color: getTheme().textLabel, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Client (optional)</label>
              <select
                value={clientId}
                onChange={e => setClientId(e.target.value)}
                style={{
                  width: '100%', background: getTheme().bgInputDark,
                  border: `1px solid ${getTheme().borderMid}`, borderRadius: 12,
                  padding: '11px 14px', fontSize: 14,
                  color: getTheme().text,
                  outline: 'none', fontFamily: 'inherit', cursor: 'pointer'
                }}
              >
                <option value=''>No client</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 20 }}>
          <button onClick={onClose} style={{
            padding: 13, borderRadius: 12, border: `1px solid ${getTheme().borderMid}`,
            background: 'transparent', color: getTheme().textSec,
            fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit'
          }}>Cancel</button>
          <button onClick={handleCreate} style={{
            padding: 13, borderRadius: 12, border: 'none',
            background: getTheme().actionBg, color: getTheme().actionText,
            fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit'
          }}>Create List</button>
        </div>
      </div>
    </div>
  );
}

// ─── Floor Plan Uploader ─────────────────────────────────────────────────────

function FloorPlanUploader({ listId, activeList, onUpdate }) {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fileName = `lists/${listId}/${generateId()}-${file.name}`;
    const { error: uploadErr } = await supabase.storage
      .from('floor-plans').upload(fileName, file, { upsert: true });
    if (!uploadErr) {
      const { data: { publicUrl } } = supabase.storage.from('floor-plans').getPublicUrl(fileName);
      // Remove old file if exists
      if (activeList?.floor_plan_path) {
        await supabase.storage.from('floor-plans').remove([activeList.floor_plan_path]);
      }
      await supabase.from('lists').update({
        floor_plan_name: file.name,
        floor_plan_url: publicUrl,
        floor_plan_path: fileName
      }).eq('id', listId);
      onUpdate({ floor_plan_name: file.name, floor_plan_url: publicUrl, floor_plan_path: fileName });
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleRemove = async () => {
    if (!window.confirm('Remove floor plan from this list?')) return;
    if (activeList?.floor_plan_path) {
      await supabase.storage.from('floor-plans').remove([activeList.floor_plan_path]);
    }
    await supabase.from('lists').update({ floor_plan_name: null, floor_plan_url: null, floor_plan_path: null }).eq('id', listId);
    onUpdate({ floor_plan_name: null, floor_plan_url: null, floor_plan_path: null });
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {activeList?.floor_plan_url ? (
        <>
          <a href={activeList.floor_plan_url} target="_blank" rel="noopener noreferrer" style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 12px', borderRadius: 10, textDecoration: 'none',
            background: getTheme().bgButton, border: `1px solid ${getTheme().borderMid}`,
            color: 'rgba(255,255,255,0.7)', fontSize: 12
          }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
            </svg>
            {activeList.floor_plan_name?.slice(0, 24)}{activeList.floor_plan_name?.length > 24 ? '…' : ''}
          </a>
          <button onClick={handleRemove} style={{
            padding: '6px 10px', borderRadius: 10, border: `1px solid ${getTheme().border}`,
            background: 'transparent', color: getTheme().textMuted,
            fontSize: 11, cursor: 'pointer', fontFamily: 'inherit'
          }}>Remove</button>
          <label style={{
            padding: '6px 10px', borderRadius: 10, border: `1px solid ${getTheme().border}`,
            background: 'transparent', color: getTheme().textMuted,
            fontSize: 11, cursor: 'pointer'
          }}>
            Replace
            <input type="file" accept=".pdf" onChange={handleUpload} style={{ display: 'none' }} />
          </label>
        </>
      ) : (
        <label style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '6px 12px', borderRadius: 10, cursor: uploading ? 'not-allowed' : 'pointer',
          background: getTheme().bgCard, border: `1px solid ${getTheme().borderMid}`,
          color: getTheme().textLabel, fontSize: 12
        }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
            <polyline points="17 8 12 3 7 8"/>
            <line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
          {uploading ? 'Uploading...' : 'Upload Floor Plan (PDF)'}
          <input type="file" accept=".pdf" onChange={handleUpload} disabled={uploading} style={{ display: 'none' }} />
        </label>
      )}
    </div>
  );
}


// ─── List Attachments ─────────────────────────────────────────────────────────

function ListAttachments({ listId }) {
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => { if (listId) loadAttachments(); }, [listId]);

  const loadAttachments = async () => {
    const { data } = await supabase.from('list_attachments')
      .select('*').eq('list_id', listId).order('created_at', { ascending: true });
    setAttachments(data || []);
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const fileName = `attachments/${listId}/${generateId()}-${safeName}`;
      const { error, data: uploadData } = await supabase.storage.from('floor-plans').upload(fileName, file);
      console.log('upload result:', error, uploadData);
      if (!error) {
        const { data: urlData } = supabase.storage.from('floor-plans').getPublicUrl(fileName);
        const publicUrl = urlData?.publicUrl;
        console.log('public url:', publicUrl);
        const { error: insertError } = await supabase.from('list_attachments').insert({
          id: generateId(), list_id: listId,
          name: file.name, url: publicUrl, path: fileName,
          type: file.type
        });
        console.log('insert error:', insertError);
      } else {
        console.log('upload error:', error);
      }
    }
    await loadAttachments();
    setUploading(false);
    e.target.value = '';
  };

  const deleteAttachment = async (att) => {
    await supabase.storage.from('floor-plans').remove([att.path]);
    await supabase.from('list_attachments').delete().eq('id', att.id);
    setAttachments(prev => prev.filter(a => a.id !== att.id));
  };

  const isImage = (att) => att.type?.startsWith('image/') || /\.(png|jpg|jpeg|gif|webp|heic)$/i.test(att.name);

  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontSize: 12, color: getTheme().textLabel, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
          Attachments {attachments.length > 0 && `(${attachments.length})`}
        </span>
        <label style={{
          display: 'flex', alignItems: 'center', gap: 5,
          padding: '5px 12px', borderRadius: 8, cursor: uploading ? 'not-allowed' : 'pointer',
          background: getTheme().bgInput, border: `1px solid ${getTheme().borderMid}`,
          color: getTheme().textSec, fontSize: 12
        }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
            <polyline points="17 8 12 3 7 8"/>
            <line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
          {uploading ? 'Uploading...' : 'Add Photos'}
          <input type="file" accept="image/*,.pdf,.doc,.docx" multiple onChange={handleUpload} disabled={uploading} style={{ display: 'none' }} />
        </label>
      </div>

      {attachments.length === 0 ? (
        <label style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 8, padding: '24px 20px', borderRadius: 14,
          border: `1px dashed ${getTheme().borderMid}`, cursor: 'pointer',
          color: getTheme().textFaint, fontSize: 12, textAlign: 'center'
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="3" y="3" width="18" height="18" rx="2"/>
            <circle cx="8.5" cy="8.5" r="1.5"/>
            <polyline points="21 15 16 10 5 21"/>
          </svg>
          Drop photos or tap to upload
          <input type="file" accept="image/*,.pdf,.doc,.docx" multiple onChange={handleUpload} style={{ display: 'none' }} />
        </label>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 8 }}>
          {attachments.map(att => (
            <div key={att.id} style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', aspectRatio: '1', background: getTheme().bgCard, border: `1px solid ${getTheme().border}`, cursor: 'pointer' }}
              onClick={() => setPreview(att)}>
              {isImage(att) ? (
                <img src={att.url} alt={att.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                  </svg>
                  <span style={{ fontSize: 9, color: getTheme().textMuted, textAlign: 'center', padding: '0 4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>{att.name}</span>
                </div>
              )}
              <button onClick={e => { e.stopPropagation(); deleteAttachment(att); }} style={{
                position: 'absolute', top: 4, right: 4,
                width: 20, height: 20, borderRadius: '50%',
                background: 'rgba(0,0,0,0.7)', border: 'none',
                color: getTheme().text, cursor: 'pointer', fontSize: 12,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>×</button>
            </div>
          ))}
          <label style={{
            aspectRatio: '1', borderRadius: 10, border: `1px dashed ${getTheme().borderMid}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: getTheme().textFaint
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            <input type="file" accept="image/*,.pdf" multiple onChange={handleUpload} style={{ display: 'none' }} />
          </label>
        </div>
      )}

      {/* Preview modal */}
      {preview && (
        <div onClick={() => setPreview(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 200, padding: 20
        }}>
          <div onClick={e => e.stopPropagation()} style={{ maxWidth: '90vw', maxHeight: '85vh', position: 'relative' }}>
            {isImage(preview) ? (
              <img src={preview.url} alt={preview.name} style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 12, objectFit: 'contain' }} />
            ) : (
              <div style={{ background: getTheme().bgModal, borderRadius: 16, padding: 32, textAlign: 'center' }}>
                <p style={{ color: getTheme().text, marginBottom: 16 }}>{preview.name}</p>
                <a href={preview.url} target="_blank" rel="noopener noreferrer" style={{
                  padding: '10px 20px', borderRadius: 10, background: getTheme().actionBg,
                  color: getTheme().actionText, fontWeight: 700, textDecoration: 'none', fontSize: 13
                }}>Open File</a>
              </div>
            )}
            <button onClick={() => setPreview(null)} style={{
              position: 'absolute', top: -12, right: -12,
              width: 28, height: 28, borderRadius: '50%',
              background: getTheme().bgButton, border: 'none',
              color: getTheme().text, cursor: 'pointer', fontSize: 16,
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>×</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Admin ───────────────────────────────────────────────────────────────

export default function Admin({ onLogout }) {
  const [theme, setTheme] = useState(() => localStorage.getItem('gearlist_theme') || 'dark');
  const [tab, setTab] = useState('lists');

  useEffect(() => {
    const dark = theme === 'dark';
    document.body.style.background = dark ? '#0e1015' : '#ffffff';
    document.body.style.color = dark ? '#ffffff' : '#0e1015';
    localStorage.setItem('gearlist_theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(t => {
    const next = t === 'dark' ? 'light' : 'dark';
    currentTheme = next;
    return next;
  });

  const T = theme === 'dark' ? {
    bgPrimary: '#0e1015', bgSecondary: '#0b0e14', bgCard: 'rgba(255,255,255,0.03)',
    bgInput: 'rgba(255,255,255,0.05)', bgInputDark: '#2a2d36', bgModal: '#181b22',
    bgButton: 'rgba(255,255,255,0.08)', bgHover: 'rgba(255,255,255,0.025)',
    border: 'rgba(255,255,255,0.08)', borderMid: 'rgba(255,255,255,0.1)',
    text: 'white', textSec: 'rgba(255,255,255,0.5)', textMuted: 'rgba(255,255,255,0.3)',
    textFaint: 'rgba(255,255,255,0.2)', textLabel: 'rgba(255,255,255,0.4)',
    divider: 'rgba(255,255,255,0.05)', actionBg: 'white', actionText: '#0e1015',
    sidebarBorder: 'rgba(255,255,255,0.07)'
  } : {
    bgPrimary: '#f4f5f7', bgSecondary: '#e8eaed', bgCard: 'rgba(0,0,0,0.03)',
    bgInput: 'rgba(0,0,0,0.05)', bgInputDark: '#e2e4e8', bgModal: '#ffffff',
    bgButton: 'rgba(0,0,0,0.06)', bgHover: 'rgba(0,0,0,0.025)',
    border: 'rgba(0,0,0,0.08)', borderMid: 'rgba(0,0,0,0.12)',
    text: '#111318', textSec: 'rgba(0,0,0,0.55)', textMuted: 'rgba(0,0,0,0.4)',
    textFaint: 'rgba(0,0,0,0.25)', textLabel: 'rgba(0,0,0,0.45)',
    divider: 'rgba(0,0,0,0.07)', actionBg: '#111318', actionText: '#ffffff',
    sidebarBorder: 'rgba(0,0,0,0.08)'
  };
  const [lists, setLists] = useState([]);
  const [activeListId, setActiveListId] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [shareUrl, setShareUrl] = useState(null);
  const [listName, setListName] = useState('New List');
  const [editingTitle, setEditingTitle] = useState(false);
  const [copiedList, setCopiedList] = useState(false);
  const [clients, setClients] = useState([]);

  const activeList = lists.find(l => l.id === activeListId);

  // Load lists on mount
  useEffect(() => {
    loadLists();
  }, []);

  // Load items when active list changes
  useEffect(() => {
    if (activeListId) loadItems(activeListId);
  }, [activeListId]);

  useEffect(() => { loadClients(); }, []);

  const loadClients = async () => {
    const { data } = await supabase.from('clients').select('id, name').order('name', { ascending: true });
    setClients(data || []);
  };

  const loadLists = async () => {
    setLoading(true);
    const { data, error } = await supabase.from('lists').select('*').order('created_at', { ascending: false });
    if (!error && data) {
      setLists(data);
      if (data.length > 0) {
        setActiveListId(data[0].id);
        setListName(data[0].name);
      }
    }
    setLoading(false);
  };

  const loadItems = async (listId) => {
    const { data, error } = await supabase.from('items').select('*').eq('list_id', listId).order('created_at', { ascending: true });
    if (!error && data) setItems(data);
  };

  const [showNewList, setShowNewList] = useState(false);

  const createList = async (name, clientId) => {
    const listName = name || `List ${lists.length + 1}`;
    const shareId = generateId() + generateId();
    const client = clients.find(c => c.id === clientId);
    const { data, error } = await supabase.from('lists').insert({
      name: listName, share_id: shareId,
      client_id: clientId || null,
      client_name: client?.name || null
    }).select().single();
    if (!error && data) {
      setLists(prev => [data, ...prev]);
      setActiveListId(data.id);
      setListName(data.name);
      setItems([]);
      setTab('lists');
    }
  };

  const updateListName = async (name) => {
    setListName(name);
    if (!activeListId) return;
    await supabase.from('lists').update({ name }).eq('id', activeListId);
    setLists(prev => prev.map(l => l.id === activeListId ? { ...l, name } : l));
  };

  const deleteList = async (listId) => {
    if (!window.confirm('Delete this list and all its items?')) return;
    await supabase.from('items').delete().eq('list_id', listId);
    await supabase.from('lists').delete().eq('id', listId);
    const remaining = lists.filter(l => l.id !== listId);
    setLists(remaining);
    if (remaining.length > 0) {
      setActiveListId(remaining[0].id);
      setListName(remaining[0].name);
    } else {
      setActiveListId(null);
      setItems([]);
    }
  };

  const addItem = async (form) => {
    const newItem = { ...form, id: generateId(), list_id: activeListId, checked: false };
    const { data, error } = await supabase.from('items').insert(newItem).select().single();
    if (!error && data) setItems(prev => [...prev, data]);
  };

  const saveItem = async (updated) => {
    await supabase.from('items').update(updated).eq('id', updated.id);
    setItems(prev => prev.map(i => i.id === updated.id ? updated : i));
  };

  const removeItem = async (id) => {
    await supabase.from('items').delete().eq('id', id);
    setItems(prev => prev.filter(i => i.id !== id));
  };

  const toggleCheck = async (id) => {
    const item = items.find(i => i.id === id);
    const updated = { ...item, checked: !item.checked };
    await supabase.from('items').update({ checked: updated.checked }).eq('id', id);
    setItems(prev => prev.map(i => i.id === id ? updated : i));
  };

  const getShareUrl = () => {
    if (!activeList) return;
    const url = `${window.location.origin}/share/${activeList.share_id}`;
    setShareUrl(url);
  };

  const copyAllText = () => {
    const groups = VENDORS.map(v => ({ vendor: v.id, items: items.filter(i => i.vendor === v.id) })).filter(g => g.items.length > 0);
    const lines = [listName, '─'.repeat(36)];
    groups.forEach(g => {
      const v = vendorOf(g.vendor);
      lines.push(`\n${v.name.toUpperCase()}`);
      g.items.forEach((item, idx) => {
        const price = item.unitPrice ? ` · $${(parseFloat(item.unitPrice)*(parseInt(item.qty)||1)).toFixed(2)}` : '';
        lines.push(`  ${idx+1}. ${item.name} ×${item.qty}${price}`);
        if (item.sku) lines.push(`     SKU: ${item.sku}`);
        if (item.notes) lines.push(`     ${item.notes}`);
        if (item.url) lines.push(`     ${item.url}`);
      });
    });
    const subtotal = items.reduce((s,i) => s+(parseFloat(i.unitPrice)||0)*(parseInt(i.qty)||1),0);
    if (subtotal > 0) lines.push(`\nTotal: $${subtotal.toFixed(2)}`);
    navigator.clipboard.writeText(lines.join('\n')).then(() => {
      setCopiedList(true);
      setTimeout(() => setCopiedList(false), 2500);
    });
  };

  // Grouped items
  const groups = VENDORS.map(v => ({
    vendor: v.id,
    items: items.filter(i => i.vendor === v.id),
  })).filter(g => g.items.length > 0);

  const subtotal = items.reduce((s, i) => s + (parseFloat(i.unitPrice)||0) * (parseInt(i.qty)||1), 0);
  const checkedCount = items.filter(i => i.checked).length;

  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="spin" style={{ width: 32, height: 32, border: '3px solid rgba(255,255,255,0.1)', borderTopColor: 'white', borderRadius: '50%' }} />
    </div>
  );

  const isMobile = window.innerWidth < 768;

  const SidebarContent = () => (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 4px', marginBottom: 20 }}>
        <div style={{ width: 28, height: 28, borderRadius: 8, background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0e1015" strokeWidth="2.5">
            <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/>
            <rect x="9" y="3" width="6" height="4" rx="1"/>
          </svg>
        </div>
        <span style={{ fontSize: 14, fontWeight: 700, flex: 1, color: 'white' }}>GearList</span>
        {isMobile && (
          <button onClick={() => setSidebarOpen(false)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: 20, padding: 4 }}>×</button>
        )}
      </div>

      {/* Nav tabs */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 20 }}>
        {[
          { id: 'lists', label: 'Lists', icon: (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/><line x1="9" y1="12" x2="15" y2="12"/><line x1="9" y1="16" x2="13" y2="16"/></svg>
          )},
          { id: 'clients', label: 'Clients', icon: (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
          )},
        ].map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); setSidebarOpen(false); }} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 10px', borderRadius: 10, border: 'none',
            background: tab === t.id ? 'rgba(255,255,255,0.08)' : 'transparent',
            color: tab === t.id ? 'white' : 'rgba(255,255,255,0.4)',
            cursor: 'pointer', fontFamily: 'inherit', fontSize: 13,
            fontWeight: tab === t.id ? 600 : 400, textAlign: 'left',
            transition: 'all 0.15s'
          }}>{t.icon}{t.label}</button>
        ))}
      </div>

      {tab === 'lists' && <>
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8, paddingLeft: 4 }}>Your Lists</div>
        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {lists.map(list => (
            <div key={list.id} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 10px', borderRadius: 10, cursor: 'pointer',
              background: list.id === activeListId ? 'rgba(255,255,255,0.07)' : 'transparent',
              transition: 'background 0.15s'
            }} onClick={() => { setActiveListId(list.id); setListName(list.name); setSidebarOpen(false); }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: 13, fontWeight: list.id === activeListId ? 600 : 400, color: list.id === activeListId ? 'white' : 'rgba(255,255,255,0.5)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{list.name}</span>
                {list.client_name && <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.2)', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{list.client_name}</span>}
              </div>
              <button onClick={e => { e.stopPropagation(); deleteList(list.id); }} style={{
                width: 20, height: 20, borderRadius: 4, border: 'none',
                background: 'transparent', color: 'rgba(255,255,255,0.2)',
                cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
              }}>×</button>
            </div>
          ))}
        </div>
        <button onClick={() => { setShowNewList(true); setSidebarOpen(false); }} style={{
          marginTop: 12, padding: '9px 12px', borderRadius: 10,
          border: '1px dashed rgba(255,255,255,0.15)', background: 'transparent',
          color: 'rgba(255,255,255,0.4)', fontSize: 12, fontWeight: 600,
          cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
        }}>+ New List</button>
      </>}

      {tab === 'clients' && <div style={{ flex: 1 }} />}

      <div style={{ marginTop: 'auto', paddingTop: 12, display: 'flex', gap: 6 }}>
        <button onClick={toggleTheme} style={{
          flex: 1, padding: '9px 12px', borderRadius: 10,
          border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.08)',
          color: 'rgba(255,255,255,0.5)', fontSize: 12, cursor: 'pointer',
          fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
        }}>
          {theme === 'dark' ? (
            <>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="5"/>
                <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
              </svg>
              Light
            </>
          ) : (
            <>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/>
              </svg>
              Dark
            </>
          )}
        </button>
        <button onClick={onLogout} style={{
          flex: 1, padding: '9px 12px', borderRadius: 10, border: 'none',
          background: 'transparent', color: 'rgba(255,255,255,0.25)',
          fontSize: 12, cursor: 'pointer', fontFamily: 'inherit'
        }}>Sign out</button>
      </div>
    </>
  );

  return (
    <div style={{ minHeight: '100vh', background: T.bgPrimary, display: 'flex' }}>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 40
        }} />
      )}

      {/* Sidebar - always dark */}
      <div style={{
        width: 220, flexShrink: 0,
        borderRight: '1px solid rgba(255,255,255,0.07)',
        background: '#0b0e14', padding: '20px 12px',
        display: 'flex', flexDirection: 'column',
        position: isMobile ? 'fixed' : 'relative',
        top: 0, left: 0, bottom: 0, zIndex: 50,
        transform: isMobile ? (sidebarOpen ? 'translateX(0)' : 'translateX(-100%)') : 'none',
        transition: 'transform 0.25s ease',
        color: 'white',
      }}>
        <SidebarContent />
      </div>

      {/* Main content */}
      <div style={{ flex: 1, overflowY: 'auto', minWidth: 0 }}>

        {/* Mobile top bar */}
        {isMobile && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '14px 16px', borderBottom: '1px solid var(--border)',
            background: T.bgSecondary, position: 'sticky', top: 0, zIndex: 30
          }}>
            <button onClick={() => setSidebarOpen(true)} style={{
              background: T.bgButton, border: 'none', borderRadius: 8,
              padding: '6px 8px', cursor: 'pointer', color: T.text,
              display: 'flex', flexDirection: 'column', gap: 4
            }}>
              <span style={{ display: 'block', width: 16, height: 2, background: T.actionBg, borderRadius: 2 }} />
              <span style={{ display: 'block', width: 16, height: 2, background: T.actionBg, borderRadius: 2 }} />
              <span style={{ display: 'block', width: 16, height: 2, background: T.actionBg, borderRadius: 2 }} />
            </button>
            <span style={{ fontSize: 15, fontWeight: 700 }}>{listName || 'GearList'}</span>
          </div>
        )}
        {tab === 'clients' ? (
          <Clients clients={clients} onClientsChange={setClients} />
        ) : !activeListId ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 12 }}>
            <p style={{ color: T.textMuted, fontSize: 14 }}>No list selected</p>
            <button onClick={() => setShowNewList(true)} style={{
              padding: '10px 20px', borderRadius: 12, border: 'none',
              background: T.actionBg, color: T.actionText, fontWeight: 700,
              fontSize: 13, cursor: 'pointer', fontFamily: 'inherit'
            }}>+ Create first list</button>
          </div>
        ) : (
          <div style={{ maxWidth: 680, margin: '0 auto', padding: '36px 20px' }}>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28 }}>
              <div>
                {editingTitle ? (
                  <input autoFocus
                    style={{ fontSize: 22, fontWeight: 700, background: 'transparent', border: 'none', borderBottom: '2px solid rgba(255,255,255,0.3)', color: T.text, outline: 'none', paddingBottom: 2, fontFamily: 'inherit' }}
                    value={listName}
                    onChange={e => setListName(e.target.value)}
                    onBlur={() => { setEditingTitle(false); updateListName(listName); }}
                    onKeyDown={e => { if (e.key === 'Enter') { setEditingTitle(false); updateListName(listName); } }}
                  />
                ) : (
                  <button onClick={() => setEditingTitle(true)} style={{
                    fontSize: 22, fontWeight: 700, background: 'none', border: 'none',
                    color: T.text, cursor: 'pointer', fontFamily: 'inherit', padding: 0,
                    display: 'flex', alignItems: 'center', gap: 8
                  }}>
                    {listName}
                    <svg style={{ opacity: 0.25 }} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                  </button>
                )}
                <p style={{ fontSize: 13, color: T.textMuted, marginTop: 4 }}>
                  {items.length === 0 ? 'No items' : `${items.length} item${items.length !== 1 ? 's' : ''} across ${groups.length} vendor${groups.length !== 1 ? 's' : ''}`}
                  {checkedCount > 0 && ` · ${checkedCount} ordered`}
                  {subtotal > 0 && <span style={{ color: T.textSec, fontWeight: 600 }}> · ${subtotal.toFixed(2)}</span>}
                </p>

                {/* Client + floor plan row */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <select
                      value={activeList?.client_id || ''}
                      onChange={async e => {
                        const clientId = e.target.value || null;
                        const clientName = clients.find(c => c.id === clientId)?.name || null;
                        await supabase.from('lists').update({ client_id: clientId, client_name: clientName }).eq('id', activeListId);
                        setLists(prev => prev.map(l => l.id === activeListId ? { ...l, client_id: clientId, client_name: clientName } : l));
                      }}
                      style={{
                        background: T.bgInputDark, border: `1px solid ${T.borderMid}`,
                        borderRadius: 10, padding: '6px 12px', fontSize: 12, color: T.text,
                        cursor: 'pointer', fontFamily: 'inherit', outline: 'none'
                      }}
                    >
                      <option value=''>No client</option>
                      {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <FloorPlanUploader
                    listId={activeListId}
                    activeList={activeList}
                    onUpdate={updated => setLists(prev => prev.map(l => l.id === activeListId ? { ...l, ...updated } : l))}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                {items.length > 0 && (
                  <>
                    <button onClick={copyAllText} style={{
                      padding: '8px 14px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                      cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s',
                      background: copiedList ? 'rgba(34,197,94,0.15)' : 'transparent',
                      color: copiedList ? '#22c55e' : 'rgba(255,255,255,0.4)',
                      border: `1px solid ${copiedList ? 'rgba(34,197,94,0.3)' : 'rgba(255,255,255,0.1)'}`
                    }}>{copiedList ? '✓ Copied' : 'Copy'}</button>

                    <button onClick={getShareUrl} style={{
                      padding: '8px 14px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                      cursor: 'pointer', fontFamily: 'inherit',
                      background: 'rgba(34,197,94,0.12)',
                      color: '#22c55e',
                      border: '1px solid rgba(34,197,94,0.3)',
                      display: 'flex', alignItems: 'center', gap: 6
                    }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                      </svg>
                      Share
                    </button>
                  </>
                )}
                <button onClick={() => setShowAdd(true)} style={{
                  padding: '8px 14px', borderRadius: 10, fontSize: 13, fontWeight: 700,
                  cursor: 'pointer', fontFamily: 'inherit',
                  background: T.actionBg, color: T.actionText, border: 'none',
                  display: 'flex', alignItems: 'center', gap: 6
                }}>
                  + Add item
                </button>
              </div>
            </div>

            {/* Empty */}
            {items.length === 0 ? (
              <div style={{
                border: `1px dashed ${T.borderMid}`, borderRadius: 20,
                padding: '60px 20px', textAlign: 'center'
              }}>
                <p style={{ color: T.textMuted, fontSize: 14, marginBottom: 16 }}>No items yet — add your first one</p>
                <button onClick={() => setShowAdd(true)} style={{
                  padding: '10px 22px', borderRadius: 12, border: 'none',
                  background: T.actionBg, color: T.actionText, fontWeight: 700,
                  fontSize: 13, cursor: 'pointer', fontFamily: 'inherit'
                }}>+ Add first item</button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {groups.map(g => (
                  <VendorGroup key={g.vendor} vendor={g.vendor} items={g.items}
                    onEdit={setEditItem} onRemove={removeItem} onToggle={toggleCheck} />
                ))}

                {subtotal > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <div style={{
                      background: T.bgCard, border: `1px solid ${T.border}`,
                      borderRadius: 16, padding: '16px 20px', minWidth: 200
                    }}>
                      {groups.map(g => {
                        const v = vendorOf(g.vendor);
                        const t = g.items.reduce((s,i) => s+(parseFloat(i.unitPrice)||0)*(parseInt(i.qty)||1),0);
                        if (!t) return null;
                        return (
                          <div key={g.vendor} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 13, color: T.textMuted }}>
                            <span style={{ color: v.color, opacity: 0.8 }}>{v.name}</span>
                            <span>${t.toFixed(2)}</span>
                          </div>
                        );
                      })}
                      <div style={{ height: 1, background: T.bgButton, margin: '10px 0' }} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 700, color: T.text }}>
                        <span>Total</span><span>${subtotal.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Attachments - always visible */}
            <ListAttachments listId={activeListId} />

            {/* Client Notes - always visible */}
            <div style={{ marginTop: 24 }}>
              <div style={{ fontSize: 12, color: T.textLabel, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, marginBottom: 8 }}>
                Client Notes
              </div>
              <textarea
                placeholder="Add notes for this client or job..."
                value={activeList?.notes || ''}
                onChange={e => {
                  const notes = e.target.value;
                  setLists(prev => prev.map(l => l.id === activeListId ? { ...l, notes } : l));
                }}
                onBlur={async e => {
                  await supabase.from('lists').update({ notes: e.target.value }).eq('id', activeListId);
                }}
                style={{
                  width: '100%', minHeight: 120,
                  background: T.bgInput, border: `1px solid ${T.borderMid}`,
                  borderRadius: 14, padding: '12px 16px',
                  fontSize: 14, color: T.text, fontFamily: "'DM Sans', sans-serif",
                  resize: 'vertical', outline: 'none', lineHeight: 1.6,
                }}
              />
            </div>
          </div>
        )}
      </div>

      {showAdd && <ItemModal onSave={addItem} onClose={() => setShowAdd(false)} />}
      {editItem && <ItemModal item={editItem} onSave={saveItem} onClose={() => setEditItem(null)} />}
      {shareUrl && <ShareModal shareUrl={shareUrl} onClose={() => setShareUrl(null)} />}
      {showNewList && <NewListModal clients={clients} onCreate={createList} onClose={() => setShowNewList(false)} />}
    </div>
  );
}
